import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { Search, Users, Target, ListTodo, BarChart3, Activity, Settings, FileText, Home, ArrowRight, Shield } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PAGES = [
  { id: 'home', label: 'Home', icon: Home, action: 'tab', tab: 'home' },
  { id: 'tasks', label: 'My Tasks', icon: ListTodo, action: 'tab', tab: 'my-tasks' },
  { id: 'leads', label: 'Leads', icon: Target, action: 'tab', tab: 'leads' },
  { id: 'clients', label: 'Clients', icon: Users, action: 'tab', tab: 'clients' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, action: 'tab', tab: 'analytics' },
  { id: 'health', label: 'Health Scores', icon: Activity, action: 'tab', tab: 'health' },
  { id: 'settings', label: 'Settings', icon: Settings, action: 'navigate', path: '/app/settings' },
  { id: 'business-admin', label: 'Business Admin', icon: Shield, action: 'navigate', path: '/app/business-admin' },
];

export default function CommandPalette({ open, onClose, onTabChange }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Search clients and leads when query changes
  useEffect(() => {
    if (!query.trim() || query.length < 2) {
      setResults([]);
      setSelectedIndex(0);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE}/api/search?q=${encodeURIComponent(query)}&limit=8`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = res.data?.results || res.data || [];
        const items = data.map(item => ({
          id: item.id,
          label: item.title || item.name || item.email,
          sublabel: item.subtitle || item.type || '',
          icon: item.type === 'lead' ? Target : item.type === 'task' ? ListTodo : item.type === 'individual' ? Users : item.type === 'entity' ? FileText : Users,
          action: 'navigate',
          path: item.url || (item.type === 'client' ? `/app/clients/${item.id}/overview` : null),
          type: item.type
        }));
        setResults(items);
        setSelectedIndex(0);
      } catch {
        setResults([]);
      }
      setLoading(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [query, token]);

  // Filter pages by query
  const filteredPages = query.trim()
    ? PAGES.filter(p => p.label.toLowerCase().includes(query.toLowerCase()))
    : PAGES;

  const allItems = [...filteredPages, ...results];

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && allItems[selectedIndex]) {
      e.preventDefault();
      executeItem(allItems[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [allItems, selectedIndex]);

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef.current?.children[selectedIndex];
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const executeItem = (item) => {
    onClose();
    if (item.action === 'tab' && onTabChange) {
      onTabChange(item.tab);
    } else if (item.action === 'navigate' && item.path) {
      navigate(item.path);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="command-palette-backdrop" />

      {/* Palette */}
      <div className="fixed top-[15%] left-1/2 -translate-x-1/2 z-[101] w-[90vw] max-w-lg" data-testid="command-palette">
        <div className="bg-white rounded-xl shadow-2xl border border-[#E6E0D9] overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E6E0D9]">
            <Search className="w-5 h-5 text-[#8C8078] shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search clients, leads, or jump to..."
              className="flex-1 text-sm text-[#2C2420] placeholder:text-[#B5AEA7] outline-none bg-transparent"
              data-testid="command-palette-input"
            />
            <kbd className="hidden sm:inline-flex text-[10px] px-1.5 py-0.5 rounded border border-[#E6E0D9] text-[#8C8078] bg-[#F9F7F5]">ESC</kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-2" data-testid="command-palette-results">
            {/* Pages section */}
            {filteredPages.length > 0 && (
              <>
                <div className="px-4 py-1 text-[10px] font-medium text-[#8C8078] uppercase tracking-wider">Pages</div>
                {filteredPages.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => executeItem(item)}
                      onMouseEnter={() => setSelectedIndex(i)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors",
                        selectedIndex === i ? "bg-[#4A3B32]/5" : "hover:bg-[#F9F7F5]"
                      )}
                      data-testid={`cmd-page-${item.id}`}
                    >
                      <Icon className="w-4 h-4 text-[#8C8078]" />
                      <span className="text-sm text-[#2C2420]">{item.label}</span>
                      {selectedIndex === i && <ArrowRight className="w-3 h-3 text-[#8C8078] ml-auto" />}
                    </button>
                  );
                })}
              </>
            )}

            {/* Search results section */}
            {results.length > 0 && (
              <>
                <div className="px-4 py-1 mt-1 text-[10px] font-medium text-[#8C8078] uppercase tracking-wider border-t border-[#E6E0D9] pt-2">Results</div>
                {results.map((item, i) => {
                  const globalIdx = filteredPages.length + i;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => executeItem(item)}
                      onMouseEnter={() => setSelectedIndex(globalIdx)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors",
                        selectedIndex === globalIdx ? "bg-[#4A3B32]/5" : "hover:bg-[#F9F7F5]"
                      )}
                      data-testid={`cmd-result-${i}`}
                    >
                      <Icon className="w-4 h-4 text-[#8C8078]" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[#2C2420] truncate">{item.label}</p>
                        {item.sublabel && <p className="text-[10px] text-[#8C8078] capitalize">{item.sublabel}</p>}
                      </div>
                      {selectedIndex === globalIdx && <ArrowRight className="w-3 h-3 text-[#8C8078] shrink-0" />}
                    </button>
                  );
                })}
              </>
            )}

            {/* Loading */}
            {loading && (
              <div className="px-4 py-3 text-sm text-[#8C8078] text-center">Searching...</div>
            )}

            {/* No results */}
            {query.length >= 2 && !loading && results.length === 0 && filteredPages.length === 0 && (
              <div className="px-4 py-6 text-sm text-[#8C8078] text-center">No results found</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
